/**
 * Workshop Notification Utilities
 * Performance-optimized constants and helpers for workshop notifications
 * 
 * @description Provides all 22 workshop notification types with icons, priorities, and messages
 */

import React from 'react';
import {
  FaUserPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCreditCard,
  FaFile,
  FaBullhorn,
  FaCalendarAlt,
  FaComments,
  FaReply,
  FaBan,
  FaTrophy,
  FaStar,
  FaUserTie,
  FaUserMinus,
  FaBell,
} from 'react-icons/fa';

// ============================================================================
// CONSTANTS - Memoized/static for zero runtime overhead
// ============================================================================

/**
 * All 22 workshop notification types
 * Use Set for O(1) lookup performance
 */
export const WORKSHOP_NOTIFICATION_TYPES = new Set([
  // Application (4)
  'workshop_application',
  'application_approved',
  'application_rejected',
  'application_pending',
  // Payment (3)
  'payment_proof_uploaded',
  'payment_approved',
  'payment_rejected',
  // Reminders (4)
  'workshop_reminder_1day',
  'workshop_reminder_1hour',
  'session_reminder',
  'workshop_starting_soon',
  // Content (3)
  'resource_uploaded',
  'announcement_posted',
  'schedule_updated',
  // Discussion (2)
  'new_discussion',
  'discussion_reply',
  // Lifecycle (3)
  'workshop_cancelled',
  'workshop_completed',
  'feedback_request',
  // Membership (4)
  'co_organizer_added',
  'participant_joined',
  'workshop_participant_joined',
  'participant_removed',
]);

/**
 * Array version for iteration (e.g., filter mapping)
 */
export const WORKSHOP_NOTIFICATION_TYPES_ARRAY = [
  'workshop_application',
  'application_approved',
  'application_rejected',
  'application_pending',
  'payment_proof_uploaded',
  'payment_approved',
  'payment_rejected',
  'workshop_reminder_1day',
  'workshop_reminder_1hour',
  'session_reminder',
  'workshop_starting_soon',
  'resource_uploaded',
  'announcement_posted',
  'schedule_updated',
  'new_discussion',
  'discussion_reply',
  'workshop_cancelled',
  'workshop_completed',
  'feedback_request',
  'co_organizer_added',
  'participant_joined',
  'workshop_participant_joined',
  'participant_removed',
];

/**
 * Priority-based styling configuration
 * Pre-computed for zero runtime calculation
 */
export const WORKSHOP_PRIORITY_STYLES = Object.freeze({
  urgent: Object.freeze({
    bgClass: 'priority-urgent',
    borderColor: '#ef4444',
    iconColor: '#ef4444',
    showToast: true,
    toastDuration: 10000,
    playSound: true,
    toastType: 'error',
  }),
  high: Object.freeze({
    bgClass: 'priority-high',
    borderColor: '#f97316',
    iconColor: '#f97316',
    showToast: true,
    toastDuration: 7000,
    playSound: true,
    toastType: 'warning',
  }),
  normal: Object.freeze({
    bgClass: 'priority-normal',
    borderColor: '#3b82f6',
    iconColor: '#3b82f6',
    showToast: true,
    toastDuration: 5000,
    playSound: false,
    toastType: 'info',
  }),
  low: Object.freeze({
    bgClass: 'priority-low',
    borderColor: '#9ca3af',
    iconColor: '#9ca3af',
    showToast: false,
    toastDuration: 3000,
    playSound: false,
    toastType: 'info',
  }),
});

// ============================================================================
// ICON MAPPING - Static object for O(1) lookup
// ============================================================================

/**
 * Pre-created icon elements for each notification type
 * Uses static className for CSS-based styling (no inline styles = better performance)
 */
const WORKSHOP_ICON_MAP = Object.freeze({
  // Application Icons
  workshop_application: <FaUserPlus className="notification-icon workshop-application" />,
  application_approved: <FaCheckCircle className="notification-icon workshop-approved" />,
  application_rejected: <FaTimesCircle className="notification-icon workshop-rejected" />,
  application_pending: <FaClock className="notification-icon workshop-pending" />,

  // Payment Icons
  payment_proof_uploaded: <FaCreditCard className="notification-icon payment-uploaded" />,
  payment_approved: <FaCheckCircle className="notification-icon payment-approved" />,
  payment_rejected: <FaTimesCircle className="notification-icon payment-rejected" />,

  // Reminder Icons
  workshop_reminder_1day: <FaClock className="notification-icon reminder-day" />,
  workshop_reminder_1hour: <FaBell className="notification-icon reminder-hour" />,
  session_reminder: <FaClock className="notification-icon session-reminder" />,
  workshop_starting_soon: <FaBell className="notification-icon workshop-starting" />,

  // Content Icons
  resource_uploaded: <FaFile className="notification-icon resource-uploaded" />,
  announcement_posted: <FaBullhorn className="notification-icon announcement" />,
  schedule_updated: <FaCalendarAlt className="notification-icon schedule-updated" />,

  // Discussion Icons
  new_discussion: <FaComments className="notification-icon new-discussion" />,
  discussion_reply: <FaReply className="notification-icon discussion-reply" />,

  // Lifecycle Icons
  workshop_cancelled: <FaBan className="notification-icon workshop-cancelled" />,
  workshop_completed: <FaTrophy className="notification-icon workshop-completed" />,
  feedback_request: <FaStar className="notification-icon feedback-request" />,

  // Membership Icons
  co_organizer_added: <FaUserTie className="notification-icon co-organizer" />,
  participant_joined: <FaUserPlus className="notification-icon participant-joined" />,
  workshop_participant_joined: <FaUserPlus className="notification-icon participant-joined" />,
  participant_removed: <FaUserMinus className="notification-icon participant-removed" />,
});

// Default icon for unknown types
const DEFAULT_WORKSHOP_ICON = <FaBell className="notification-icon default" />;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * O(1) lookup to check if a notification type is a workshop notification
 * @param {string} type - Notification type
 * @returns {boolean}
 */
export const isWorkshopNotificationType = (type) => WORKSHOP_NOTIFICATION_TYPES.has(type);

/**
 * Get icon for workshop notification type
 * O(1) lookup with fallback
 * @param {string} type - Notification type
 * @returns {React.ReactElement}
 */
export const getWorkshopNotificationIcon = (type) => {
  return WORKSHOP_ICON_MAP[type] || DEFAULT_WORKSHOP_ICON;
};

/**
 * Get priority styles for a notification
 * @param {string} priority - Priority level (urgent, high, normal, low)
 * @returns {Object}
 */
export const getWorkshopPriorityStyles = (priority) => {
  return WORKSHOP_PRIORITY_STYLES[priority] || WORKSHOP_PRIORITY_STYLES.normal;
};

/**
 * Generate user-friendly message for workshop notification
 * Memoization-friendly pure function
 * @param {Object} notification - Notification object
 * @returns {string}
 */
export const getWorkshopNotificationMessage = (notification) => {
  if (!notification) return '';

  const { type, data = {}, workshop = {}, sender = {} } = notification;
  const workshopTitle = workshop.title || data.workshopTitle || 'Workshop';
  const senderName = sender.name || data.senderName || 'Someone';

  switch (type) {
    // Application
    case 'workshop_application':
      return `${senderName} applied to join "${workshopTitle}"`;
    case 'application_approved':
      return `🎉 Your application to "${workshopTitle}" was approved!`;
    case 'application_rejected':
      return `Your application to "${workshopTitle}" was not approved${data.rejectionReason ? `: ${data.rejectionReason}` : ''}`;
    case 'application_pending':
      return `Your application to "${workshopTitle}" is pending review`;

    // Payment
    case 'payment_proof_uploaded':
      return `${senderName} uploaded payment proof for "${workshopTitle}"`;
    case 'payment_approved':
      return `✅ Your payment for "${workshopTitle}" was verified!`;
    case 'payment_rejected':
      return `Payment verification failed for "${workshopTitle}"${data.rejectionReason ? `: ${data.rejectionReason}` : ''}`;

    // Reminders
    case 'workshop_reminder_1day':
      return `📅 "${workshopTitle}" starts tomorrow!`;
    case 'workshop_reminder_1hour':
      return `⏰ "${workshopTitle}" starts in 1 hour!`;
    case 'session_reminder':
      return `🔔 Session reminder for "${workshopTitle}"`;
    case 'workshop_starting_soon':
      return `🚀 "${workshopTitle}" is starting soon!`;

    // Content
    case 'resource_uploaded':
      return `📚 New resource added to "${workshopTitle}"${data.resourceId ? `: ${data.resourceTitle || 'Resource'}` : ''}`;
    case 'announcement_posted':
      return `📢 New announcement in "${workshopTitle}"`;
    case 'schedule_updated':
      return `📅 Schedule updated for "${workshopTitle}"`;

    // Discussion
    case 'new_discussion':
      return `💬 New discussion started in "${workshopTitle}"`;
    case 'discussion_reply':
      return `💬 ${senderName} replied to your discussion in "${workshopTitle}"`;

    // Lifecycle
    case 'workshop_cancelled':
      return `⚠️ "${workshopTitle}" has been cancelled`;
    case 'workshop_completed':
      return `🏆 Congratulations! "${workshopTitle}" is complete`;
    case 'feedback_request':
      return `⭐ Please share your feedback for "${workshopTitle}"`;

    // Membership
    case 'co_organizer_added':
      return `🎯 You've been added as co-organizer for "${workshopTitle}"`;
    case 'participant_joined':
    case 'workshop_participant_joined':
      return `👋 ${senderName} joined "${workshopTitle}"`;
    case 'participant_removed':
      return `You've been removed from "${workshopTitle}"${data.removalReason ? `: ${data.removalReason}` : ''}`;

    default:
      return notification.message || 'New workshop notification';
  }
};

/**
 * Get action URL for workshop notification
 * @param {Object} notification - Notification object
 * @returns {string|null}
 */
export const getWorkshopActionUrl = (notification) => {
  if (!notification) return null;

  const { type, data = {}, workshop = {}, actionUrl } = notification;
  
  // Use provided actionUrl if available
  if (actionUrl) return actionUrl;

  const workshopId = workshop._id || data.workshopId;
  if (!workshopId) return null;

  switch (type) {
    case 'workshop_application':
    case 'payment_proof_uploaded':
      return `/workshops/${workshopId}/participants`;
    
    case 'application_approved':
    case 'payment_approved':
    case 'workshop_reminder_1day':
    case 'workshop_reminder_1hour':
    case 'workshop_starting_soon':
    case 'workshop_completed':
    case 'co_organizer_added':
      return `/workshops/${workshopId}`;
    
    case 'payment_rejected':
      return `/workshops/${workshopId}/payment-proof`;
    
    case 'resource_uploaded':
      return `/workshops/${workshopId}/resources`;
    
    case 'announcement_posted':
      return `/workshops/${workshopId}/announcements`;
    
    case 'schedule_updated':
      return `/workshops/${workshopId}/schedule`;
    
    case 'new_discussion':
    case 'discussion_reply':
      const discussionId = data.discussionId;
      return discussionId 
        ? `/workshops/${workshopId}/discussions/${discussionId}`
        : `/workshops/${workshopId}/discussions`;
    
    case 'feedback_request':
      return `/workshops/${workshopId}/feedback`;
    
    case 'session_reminder':
      return `/workshops/${workshopId}`;

    default:
      return workshopId ? `/workshops/${workshopId}` : null;
  }
};

/**
 * Check if notification requires action
 * @param {Object} notification - Notification object
 * @returns {boolean}
 */
export const isActionRequired = (notification) => {
  if (!notification) return false;
  
  // Use explicit actionRequired flag if provided
  if (notification.actionRequired !== undefined) {
    return notification.actionRequired;
  }

  // Infer from type
  const actionRequiredTypes = new Set([
    'workshop_application',
    'application_approved',
    'payment_proof_uploaded',
    'payment_approved',
    'payment_rejected',
    'workshop_reminder_1hour',
    'workshop_starting_soon',
    'schedule_updated',
    'feedback_request',
    'co_organizer_added',
  ]);

  return actionRequiredTypes.has(notification.type);
};

export default {
  WORKSHOP_NOTIFICATION_TYPES,
  WORKSHOP_NOTIFICATION_TYPES_ARRAY,
  WORKSHOP_PRIORITY_STYLES,
  isWorkshopNotificationType,
  getWorkshopNotificationIcon,
  getWorkshopPriorityStyles,
  getWorkshopNotificationMessage,
  getWorkshopActionUrl,
  isActionRequired,
};
